import {
  BlotterAggFunc,
  Button,
  columnTypes,
  createCsvTextAggFunc,
  dateComparator,
  formattedDate,
  IconName,
  useStrategiesContext,
  type ColumnDef,
  type ColumnOrColumnGroup,
} from '@talos/kyoko';
import type {
  ColDef,
  GridOptions,
  GroupCellRendererParams,
  ICellRendererParams,
  IRowNode,
  ValueGetterParams,
} from 'ag-grid-community';
import { useMemo } from 'react';
import type { PreTradeResponseData } from '../../redux/preTradeApiTypes';
import { formatStrategyDisplayName } from '../../utils';

export const HISTORY_ROW_COUNT_COLID = 'HistoryRowCountColID';
const HISTORY_STRATEGIES_AGG_FUNC = 'historyStrategiesCsvText';

interface UseHistoryColumnsParams {
  onOpenSession: (sessionId: string) => void;
  openSessionId: string;
  switchToScenarios: () => void;
}

export function useHistoryColumns({ onOpenSession, openSessionId, switchToScenarios }: UseHistoryColumnsParams): {
  columns: ColumnOrColumnGroup[];
  autoGroupColumnDef: ColDef<PreTradeResponseData>;
  aggFuncs: GridOptions<PreTradeResponseData>['aggFuncs'];
} {
  const { strategiesByName } = useStrategiesContext();

  const columns = useMemo(
    () =>
      [
        {
          field: 'SessionIdentifier',
          type: 'text',
          rowGroup: true,
          hide: true,
        },
        {
          field: 'Timestamp',
          type: 'date',
          hide: true,
          // Group rows show the most recent timestamp among their children.
          initialAggFunc: BlotterAggFunc.LatestTimestamp,
        },
        {
          type: 'rowCount',
          id: HISTORY_ROW_COUNT_COLID,
          title: '# Scenarios',
          width: 100,
          aggregate: true,
        },
        {
          field: 'Symbol',
          type: 'security',
          sortable: true,
          minWidth: 160,
          initialAggFunc: BlotterAggFunc.CsvText,
        },
        {
          field: 'Strategy',
          type: 'text',
          title: 'Strategies',
          sortable: true,
          minWidth: 160,
          params: {
            getLabel: (value: string) => formatStrategyDisplayName(value, strategiesByName) ?? value,
          },
          // Group rows show a comma-separated list of unique strategy display names.
          initialAggFunc: HISTORY_STRATEGIES_AGG_FUNC,
        },
        {
          type: 'custom',
          id: 'open-session',
          pinned: 'right',
          width: 100,
          params: {
            cellRenderer: OpenSessionCellRenderer,
            cellRendererParams: {
              onOpenSession: (sessionId: string) =>
                sessionId === openSessionId ? switchToScenarios() : onOpenSession(sessionId),
            },
            sortable: false,
            lockVisible: true,
            lockPinned: true,
            headerName: '',
            suppressHeaderMenuButton: true,
            suppressHeaderContextMenu: true,
          },
        },
      ] satisfies ColumnDef<PreTradeResponseData>[],
    [strategiesByName, onOpenSession, openSessionId, switchToScenarios]
  );

  // The auto-group column replaces the SessionIdentifier row-group column. It shows the
  // aggregated session timestamp as a collapsible group header, sorted newest-first.
  const autoGroupColumnDef = useMemo(
    () =>
      ({
        ...columnTypes.group({
          type: 'group',
          id: 'SessionGroup',
          editable: false,
          hide: false,
          suppressColumnsToolPanel: false,
          params: {
            suppressCount: true,
            headerValueGetter: () => 'Timestamp',
          },
        }),
        // Show the aggregated timestamp for group rows, raw timestamp for leaves.
        valueGetter: (params: ValueGetterParams<PreTradeResponseData>) =>
          params.node?.group ? params.node.aggData?.Timestamp : params.data?.Timestamp,
        // Sort group rows by their aggregated timestamp. Return 0 for leaves to preserve their order.
        comparator: (_valueA, _valueB, nodeA: IRowNode, nodeB: IRowNode) =>
          nodeA.group ? dateComparator(nodeA.aggData?.Timestamp, nodeB.aggData?.Timestamp) : 0,

        // Override group column's cellRendererSelector — use ag-grid's built-in group renderer directly.
        cellRendererSelector: undefined,
        cellRenderer: 'agGroupCellRenderer',
        cellRendererParams: {
          suppressCount: true,
          innerRenderer: TimestampInnerRenderer,
        },

        sort: 'desc' as const,
      }) as ColDef<PreTradeResponseData>,
    []
  );

  const aggFuncs = useMemo(
    () => ({
      [HISTORY_STRATEGIES_AGG_FUNC]: createCsvTextAggFunc<PreTradeResponseData>(
        value => formatStrategyDisplayName(value, strategiesByName) ?? value
      ),
    }),
    [strategiesByName]
  );

  return { columns, autoGroupColumnDef, aggFuncs };
}

// ---- RENDERERS ----

function OpenSessionCellRenderer(
  params: ICellRendererParams<PreTradeResponseData> & { onOpenSession: (sessionId: string) => void }
) {
  const sessionId = params.node.group ? params.node.key : undefined;
  if (!sessionId) {
    return null;
  }
  return (
    <Button ghost startIcon={IconName.ExternalLink} onClick={() => params.onOpenSession(sessionId)}>
      Open
    </Button>
  );
}

function TimestampInnerRenderer(params: GroupCellRendererParams<PreTradeResponseData>) {
  const timestamp = params.node.group ? params.node.aggData?.Timestamp : params.data?.Timestamp;
  return <>{formattedDate(timestamp)}</>;
}
